import { Navigate, Outlet } from 'react-router-dom';
import { useSession } from '../auth/authQueries';
import { ROUTES } from '../lib/routes';
import { toSafeTarget } from '../lib/safeNavigation';
import { CenteredCardLayout } from '../components/layout/CenteredCardLayout';
import { Spinner } from '../components/ui';

function FullPageLoading({ label }: { label: string }) {
  return (
    <CenteredCardLayout>
      <div className="stack" style={{ alignItems: 'center', textAlign: 'center' }}>
        <Spinner label={label} />
        <p className="muted" aria-live="polite">
          {label}
        </p>
      </div>
    </CenteredCardLayout>
  );
}

/**
 * Gate for authenticated routes (spec §12.2). Anonymous -> /login (replace).
 * PENDING_ONBOARDING -> /onboarding. Otherwise render the matched route.
 */
export function RequireAuthenticated() {
  const session = useSession();

  if (session.isLoading) return <FullPageLoading label="Đang tải…" />;
  if (!session.data || !session.data.authenticated) {
    return <Navigate to={ROUTES.login} replace />;
  }
  if (session.data.user.status === 'PENDING_ONBOARDING') {
    return <Navigate to={ROUTES.onboarding} replace />;
  }
  return <Outlet />;
}

/** Gate for /onboarding — only PENDING_ONBOARDING users (spec §12.2). */
export function RequirePendingOnboarding() {
  const session = useSession();

  if (session.isLoading) return <FullPageLoading label="Đang tải…" />;
  if (!session.data || !session.data.authenticated) {
    return <Navigate to={ROUTES.login} replace />;
  }
  if (session.data.user.status !== 'PENDING_ONBOARDING') {
    const next = toSafeTarget(session.data.nextUrl, ROUTES.account);
    return <Navigate to={next} replace />;
  }
  return <Outlet />;
}

/** Gate for /login — authenticated ACTIVE users skip to account/nextUrl. */
export function AnonymousOnly() {
  const session = useSession();

  if (session.isLoading) return <FullPageLoading label="Đang tải…" />;
  if (session.data?.authenticated) {
    if (session.data.user.status === 'PENDING_ONBOARDING') {
      return <Navigate to={ROUTES.onboarding} replace />;
    }
    const next = toSafeTarget(session.data.nextUrl, ROUTES.account);
    return <Navigate to={next} replace />;
  }
  return <Outlet />;
}
